import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AccordionDropdown, getCollegeIcon } from '@backrs_ui/common-ui';

import { FilterOption, MajorCollegeSummary } from '@backrs_ui/grow-ui/src/pages/MajorsGuide/types';
import { groupMajorsByCollege } from '@backrs_ui/grow-ui/src/pages/MajorsGuide/util';

import { Box, SxProps, Theme } from '@mui/material';

type MajorsByCollegeProps = Readonly<{
  majors: MajorCollegeSummary[];
  /** College types from filters API - defines display order and labels */
  collegeTypes: FilterOption[];
  isLoading?: boolean;
  /** Controlled expanded state - pass null to close all */
  expandedCollege?: string | null;
  /** Called when expanded college changes */
  onExpandedCollegeChange?: (college: string | null) => void;
}>;

export const MajorsByCollege = ({
  majors,
  collegeTypes,
  isLoading = false,
  expandedCollege: controlledExpandedCollege,
  onExpandedCollegeChange,
}: MajorsByCollegeProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  // Track which accordion is currently expanded (null = none)
  // Support both controlled and uncontrolled modes
  const [internalExpandedCollege, setInternalExpandedCollege] = useState<string | null>(null);
  const isControlled = controlledExpandedCollege !== undefined;
  const expandedCollege = isControlled ? controlledExpandedCollege : internalExpandedCollege;

  const handleMajorClick = (major: MajorCollegeSummary) => {
    navigate(`/majors/${major.id}`);
  };

  // Handle accordion expansion change - only one can be open at a time
  const handleAccordionChange =
    (collegeValue: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      const newValue = isExpanded ? collegeValue : null;

      if (isControlled) {
        onExpandedCollegeChange?.(newValue);
      } else {
        setInternalExpandedCollege(newValue);
      }
    };

  // Close accordion when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (isControlled) {
          onExpandedCollegeChange?.(null);
        } else {
          setInternalExpandedCollege(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isControlled, onExpandedCollegeChange]);

  // Group majors by college value (matches major.typicalCollege)
  const collegeValues = useMemo(() => collegeTypes.map((ct) => ct.value), [collegeTypes]);
  const groupedMajors = useMemo(
    () => groupMajorsByCollege(majors, collegeValues),
    [majors, collegeValues],
  );

  // Create lookup from value to label for display
  const collegeLabelMap = useMemo(
    () => new Map(collegeTypes.map((ct) => [ct.value, ct.label])),
    [collegeTypes],
  );

  if (isLoading || collegeTypes.length === 0) {
    return null;
  }

  return (
    <Box ref={containerRef} sx={containerSx}>
      <Box sx={accordionGridSx}>
        {Array.from(groupedMajors.entries()).map(([collegeValue, collegeMajors]) => {
          // Skip empty colleges
          if (collegeMajors.length === 0) return null;

          // Get display label (falls back to value if not found)
          const displayLabel = collegeLabelMap.get(collegeValue) ?? collegeValue;

          // Create subtitle strings (just major names, no icons)
          const subtitles = collegeMajors.map((major) => major.name);

          return (
            <Box key={collegeValue} sx={accordionWrapperSx}>
              <AccordionDropdown
                title={displayLabel}
                iconName={getCollegeIcon(collegeValue)}
                subtitles={subtitles}
                expanded={expandedCollege === collegeValue}
                onChange={handleAccordionChange(collegeValue)}
                onSubtitleClick={(majorName) => {
                  const major = collegeMajors.find((m) => m.name === majorName);

                  if (major) {
                    handleMajorClick(major);
                  }
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const accordionGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-2xl'],
  alignItems: 'start',
  '@container (min-width: 600px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const accordionWrapperSx: SxProps<Theme> = {
  minWidth: 0,
};
